import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const UnreadMessagesContext = createContext();

export const useUnreadMessages = () => {
  const context = useContext(UnreadMessagesContext);
  if (!context) {
    throw new Error('useUnreadMessages must be used within an UnreadMessagesProvider');
  }
  return context;
};

export const UnreadMessagesProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      setUser(firebaseUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setUnreadCount(0);
      return;
    }

    let unsubscribers = [];
    let currentMatches = [];
    let unreadCounts = {}; // Track unread count per match

    // Function to calculate total unread count
    const updateTotalUnreadCount = () => {
      const total = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
      setUnreadCount(total);
    };

    // Listen to user's matches
    const matchesRef = collection(db, 'users', user.uid, 'matches');
    const unsubscribeMatches = onSnapshot(matchesRef, (snapshot) => {
      currentMatches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Clear previous chat listeners
      unsubscribers.forEach(unsub => unsub());
      unsubscribers = [];
      unreadCounts = {}; // Reset unread counts
      
      // Set up real-time listeners for each match's chat
      currentMatches.forEach(match => {
        const chatRef = collection(
          db, 'tournament-v2', 'upcoming', 'list', match.tournamentId,
          'TournamentSchedule', match.id, 'chat'
        );
        
        const unsubscribeChat = onSnapshot(chatRef, (chatSnapshot) => {
          const messages = chatSnapshot.docs.map(doc => doc.data());
          const opponentId = user.uid === match.playerAId ? match.playerBId : match.playerAId;
          
          // Count unread messages from opponent and admin for this specific match
          const unreadMessages = messages.filter(msg => 
            (msg.senderId === opponentId || msg.isAdminMessage) && 
            (!msg.readBy || !msg.readBy[user.uid])
          );
          
          // Update unread count for this match
          unreadCounts[match.id] = unreadMessages.length;
          
          // Update total unread count
          updateTotalUnreadCount();
        });
        
        unsubscribers.push(unsubscribeChat);
      });
    });

    return () => {
      unsubscribeMatches();
      unsubscribers.forEach(unsub => unsub());
    };
  }, [user?.uid]);

  const markAllAsRead = async () => {
    if (!user?.uid) return;

    try {
      // Get all user's matches
      const matchesRef = collection(db, 'users', user.uid, 'matches');
      const matchesSnapshot = await getDocs(matchesRef);
      const matches = matchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Mark all messages as read for each match
      for (const match of matches) {
        try {
          const chatRef = collection(
            db, 'tournament-v2', 'upcoming', 'list', match.tournamentId,
            'TournamentSchedule', match.id, 'chat'
          );
          
          const chatSnapshot = await getDocs(chatRef);
          const messages = chatSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          const opponentId = user.uid === match.playerAId ? match.playerBId : match.playerAId;
          const unreadMessages = messages.filter(msg => 
            (msg.senderId === opponentId || msg.isAdminMessage) && 
            (!msg.readBy || !msg.readBy[user.uid])
          );

          // Mark each unread message as read
          for (const message of unreadMessages) {
            const messageRef = doc(db, 'tournament-v2', 'upcoming', 'list', match.tournamentId, 'TournamentSchedule', match.id, 'chat', message.id);
            await updateDoc(messageRef, {
              [`readBy.${user.uid}`]: serverTimestamp(),
              status: 'read'
            });
          }
        } catch (error) {
          console.error('Error marking messages as read for match:', error);
        }
      }

      // Reset unread count
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  const value = {
    unreadCount,
    setUnreadCount,
    markAllAsRead
  };

  return (
    <UnreadMessagesContext.Provider value={value}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};
